/* eslint-disable complexity */
import {useActivateApiAbTestCallback} from '@aboutyou/feature-flags';
import {ProductTilePreferences} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_preferences.types';
import {StreamTracker} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/tracker.types';
import {
  GetStreamResponse as GetBrandStreamResponse,
  GetShopStreamResponse,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/brand_hub/v1/service.types';
import {
  GetAdditionalProductStreamResponse,
  GetAdditionalProductStreamV2Response,
  GetProductStreamResponse,
  GetProductStreamV2Response,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/service.types';
import {TeaserTileCarousel_TextPosition} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/stream/stream.types';
import {GetStreamResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coupons/v1/landingpage/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useIsCategoryPage} from '@aboutyou/router';
import {useTrackFeature, useUpdateSortingChannel} from '@aboutyou/tracking';
import {
  ErrorBoundary,
  ImageProps,
  ProductTileEndOfStream,
  Slot,
} from '@aboutyou/ui-components';
import React, {memo, useCallback, useEffect, useMemo} from 'react';
import shallowEqual from 'shallowequal';
import styled, {css} from 'styled-components';
import {BrandPageHighlights} from '../../backend-services/BrandHub/BrandPageHighlights/BrandPageHighlights';
import {DeprecatedFashionUpdate} from '../DeprecatedFashionUpdate/FashionUpdate';
import {OutfitTile} from '../OutfitTile/OutfitTile';
import {ProductTile, ProductTileProps} from '../ProductTile/ProductTile';
import {StreamProductTileWrapper} from '../ProductTile/StreamProductTileWrapper';
import {ShallowRenderingState} from '../ProductTile/utils';
import {SponsoredContentLane} from '../SponsoredContentLane/SponsoredContentLane';
import {TeaserCarousel} from '../TeaserCarousel/TeaserCarousel';
import {EndOfStreamWithCTA} from './EndOfStreamWithCTA';
import {CategoryTeaser} from './Tiles/CategoryTeaser/CategoryTeaser';
import {HeroElementSlider} from './Tiles/HeroElements/HeroElementSlider';
import {HeroElementTile} from './Tiles/HeroElements/HeroElementTile';
import {InStreamPushNotificationToggle} from './Tiles/InStreamPushNotificationToggle';

import {OnLoadTriggerTile} from './Tiles/OnLoadTriggerTile';
import {OutfitSliderTile} from './Tiles/OutfitSliderTile';
import {StreamAdvertisementNormalized} from './Tiles/StreamAdvertisementNormalized';
import {StreamDescription} from './Tiles/StreamDescription';
import {StreamProgressPositioned} from './Tiles/StreamProgressPositioned';
import {UspSliderNormalized} from './Tiles/UspSliderNormalized';
import {NonProductTileSpacingAdjustment} from './helpers';

export interface StreamTileListProps {
  gridColumns: number | undefined;
  inlineBASPlacements?: boolean;
  constrainStoryTilesToViewportBoundary?: boolean;
  shallowRenderingState?: ShallowRenderingState;
  uspItemsToDisplayPerSlide?: number;
  onClickAddCta?: ({
    productId,
    filteredSizeId,
    couponPromotionId,
    recoType,
    streamPosition,
    locationId,
  }: {
    productId: number;
    filteredSizeId?: number;
    couponPromotionId?: string;
    recoType?: string;
    streamPosition?: number;
    locationId?: string;
  }) => void;
  addCtaVisibility?: 'always' | 'hover' | 'never';
  addCtaTitle?: string;
  showLowStockHint?: boolean;
  onTileVisible?: () => void;
  disableImagePreloading?: boolean;
  layoutConfig?: {
    hideSliderSideButtons?: boolean;
    layout?: 'desktop' | 'mobile';
  };
  hideTopUSP?: boolean;
  productTileGalleryMode?: 'hover' | 'always';
  interactiveHoverMode?: boolean;
  enableImageGalleryAnimation?: boolean;
  selectedCoupon?: string;
  ProductTileComponent?: React.FC<ProductTileProps>;
  enableOutfitSlider?: boolean;
  itemsPreferences?: ProductTilePreferences;
}

// We only receive stream tracking data from the GetProductStreamV2 endpoint (used for the first page).
// For subsequent category pages, we use the GetProductStreamPageV2 endpoint, which doesn't include any tracking data.
// Therefore, we need to store the sortingChannel from the first request and reuse it for the following pages.
let streamTrackingData: StreamTracker | undefined;

const StreamTileList: React.FC<
  (
    | GetProductStreamResponse
    | GetProductStreamV2Response
    | GetAdditionalProductStreamResponse
    | GetAdditionalProductStreamV2Response
    | GetShopStreamResponse
    | GetStreamResponse
    | GetBrandStreamResponse
  ) &
    StreamTileListProps
> = ({
  items,
  gridColumns,
  inlineBASPlacements,
  constrainStoryTilesToViewportBoundary,
  shallowRenderingState,
  uspItemsToDisplayPerSlide,
  onClickAddCta,
  addCtaTitle,
  onTileVisible,
  disableImagePreloading,
  layoutConfig,
  productTileGalleryMode,
  interactiveHoverMode,
  enableImageGalleryAnimation,
  selectedCoupon,
  ProductTileComponent = ProductTile,
  enableOutfitSlider,
  ...rest
}) => {
  const trackFeature = useTrackFeature();
  const isCategoryPage = useIsCategoryPage();

  useProductNamesABTest(isCategoryPage);

  const handleClickAddCta = useCallback(
    ({
        filteredSizeId,
        productId,
        couponPromotionId,
        recoType,
        streamPosition,
      }: {
        filteredSizeId?: number;
        productId: number;
        couponPromotionId?: string;
        recoType?: string;
        streamPosition?: number;
      }) =>
      () => {
        onClickAddCta?.({
          productId,
          filteredSizeId,
          couponPromotionId,
          recoType,
          streamPosition,
          locationId: recoType,
        });
        trackFeature({
          eventType: 'click',
          label: `add_to_basket_stream|${productId}`,
          name: 'add_to_basket_stream',
        });
      },
    [onClickAddCta],
  );

  const handleColorClick = useCallback(
    (productId: number, recoType?: string) => {
      if (interactiveHoverMode) {
        trackFeature({
          eventType: 'click',
          label: `category_page_color_picker`,
        });
        onClickAddCta?.({
          productId,
          locationId: recoType,
        });
      }
    },
    [],
  );

  const {t} = useTranslation();

  const firstTileIndex = useMemo(() => {
    const firstProductIndex = items.findIndex(
      item => item.type?.$case === 'productSection',
    );
    return firstProductIndex === -1 ? 0 : firstProductIndex;
  }, [items]);

  streamTrackingData =
    'trackingData' in rest ? rest.trackingData : streamTrackingData;

  useUpdateSortingChannel(streamTrackingData?.sortingChannel);

  return (
    <>
      {items.map((tile, index) => {
        const streamPosition =
          'streamPosition' in tile ? tile.streamPosition : index;

        const productTileTrackingData = {
          ...(streamTrackingData || {}),
          recoType: 'product_stream',
        };

        switch (tile.type?.$case) {
          case 'productSection': {
            const productTile = tile.type.productSection.productTile;
            const sectionTracker =
              'sectionTracker' in tile.type.productSection
                ? tile.type.productSection.sectionTracker
                : undefined;
            const productId = productTile.productId;

            productTileTrackingData.recoType =
              sectionTracker?.recoType || productTileTrackingData.recoType;
            return (
              <StreamProductTileWrapper
                key={`productSection-${productId}`}
                productId={productId}
                shallowRenderingState={shallowRenderingState}
                renderTile={({isInViewOnRemount, tileRef}) => {
                  return (
                    <ProductTileComponent
                      category={'category' in rest ? rest.category : undefined}
                      isInViewOnRemount={isInViewOnRemount}
                      tileRef={
                        tileRef as React.RefObject<HTMLDivElement | null>
                      }
                      tile={productTile}
                      importance={
                        disableImagePreloading
                          ? 'auto'
                          : imageLoadImportance({
                              streamPosition,
                              gridColumns,
                            })
                      }
                      streamPosition={streamPosition}
                      trackingData={productTileTrackingData}
                      enhanceSpeed={true}
                      onClickAddCta={
                        onClickAddCta
                          ? handleClickAddCta({
                              productId,
                              filteredSizeId: productTile.selectedSizeId,
                              couponPromotionId: productTile.couponPromotionId,
                              recoType:
                                sectionTracker?.recoType ||
                                productTileTrackingData.recoType,
                              streamPosition,
                            })
                          : undefined
                      }
                      gridColumns={gridColumns}
                      addCtaLabel={addCtaTitle ?? t('Add to basket')}
                      onColorClick={handleColorClick}
                      productTileGallery={productTileGalleryMode === 'always'}
                      selectedCoupon={selectedCoupon}
                      enableImageGalleryAnimation={
                        index === firstTileIndex && enableImageGalleryAnimation
                      }
                    />
                  );
                }}
              />
            );
          }
          // deprecated - sponsoredContent is the new one
          case 'streamAdvertisement':
            return (
              <StyledStreamAdvertisementNormalized
                $increasePadding={Boolean(interactiveHoverMode)}
                key={`streamAdvertisement-${streamPosition}`}
                tile={tile.type.streamAdvertisement}
                gridColumns={gridColumns}
                streamPosition={streamPosition}
                inlineBASPlacements={inlineBASPlacements}
                basInfoIconLink={
                  tile.type.streamAdvertisement.infoIcon?.cta?.linkTarget.url
                }
              />
            );
          case 'sponsoredContent':
            return (
              <StyledCategoryTeaser
                key={`categoryTeaser-${streamPosition}`}
                $increasePadding={Boolean(interactiveHoverMode)}
                gridColumns={gridColumns}
                streamPosition={streamPosition}
                inlineBASPlacements={inlineBASPlacements}
                {...tile.type.sponsoredContent}
              />
            );
          case 'streamDescription':
            return (
              <StreamDescription
                key={`streamDescription-${streamPosition}`}
                tile={tile.type.streamDescription}
                gridColumns={gridColumns}
                streamPosition={streamPosition}
              />
            );
          case 'streamProgress':
          case 'progress':
            return (
              <StreamProgressPositioned
                key={`streamProgress-${streamPosition}`}
                gridColumns={gridColumns}
                streamPosition={streamPosition}
                tile={
                  tile.type?.$case === 'streamProgress'
                    ? tile.type.streamProgress
                    : tile.type.progress
                }
              />
            );
          case 'endOfStream': {
            if ('headline' in tile.type.endOfStream) {
              return (
                <Slot
                  columns={gridColumns}
                  position={streamPosition}
                  key={`endOfStream-${streamPosition}`}
                  Comp="li"
                >
                  <EndOfStreamWithCTA endOfStream={tile.type.endOfStream} />
                </Slot>
              );
            }

            return (
              <ProductTileEndOfStream
                gridColumns={gridColumns}
                key={`endOfStream-${streamPosition}`}
                endOfStreamDescription={tile.type.endOfStream.message}
              />
            );
          }
          case 'uspBanner': {
            return (
              <UspSliderNormalized
                key={`uspBanner-${streamPosition}`}
                gridColumns={gridColumns}
                streamPosition={streamPosition}
                usps={tile.type.uspBanner.usps}
                uspItemsToDisplayPerSlide={uspItemsToDisplayPerSlide}
              />
            );
          }
          case 'sponsoredContentLane': {
            return (
              <ErrorBoundary
                key={`teaserTileCarousel-${streamPosition}`}
                ErrorComponent={() => null}
                renderContext="streamView_teaserCarousel"
              >
                <SponsoredContentLane
                  gridColumns={gridColumns}
                  streamPosition={streamPosition}
                  constrainStoryTilesToViewportBoundary={
                    constrainStoryTilesToViewportBoundary
                  }
                  uniqueKey={`streamView_sponsoredContentLane_${streamPosition}`}
                  shallowRenderingState={shallowRenderingState}
                  {...tile.type.sponsoredContentLane}
                />
              </ErrorBoundary>
            );
          }
          case 'pushNotificationTrigger':
            return (
              <Slot
                key={`pushNotificationTrigger-${streamPosition}`}
                columns={gridColumns}
                position={streamPosition}
                Comp="li"
              >
                <OnLoadTriggerTile key={index} callback={onTileVisible} />
              </Slot>
            );
          case 'fashionUpdate': {
            const fashionUpdateTrackingData = {
              recoType: tile.type.fashionUpdate.tracker.name,
              sortingChannel: streamTrackingData?.sortingChannel ?? '',
            };

            return (
              <DeprecatedFashionUpdate
                key={`fashionUpdate-${streamPosition}`}
                gridColumns={gridColumns}
                streamPosition={streamPosition}
                headline={tile.type.fashionUpdate.headline}
                productTiles={tile.type.fashionUpdate.productTiles}
                trackingData={fashionUpdateTrackingData}
                hideFashionUpdateSideButtons={
                  layoutConfig?.hideSliderSideButtons
                }
              />
            );
          }
          case 'inStreamPushNotificationTrigger':
            return (
              <Slot
                columns={gridColumns}
                position={streamPosition}
                key={`pushNotificationTrigger-${streamPosition}`}
                Comp="li"
              >
                <InStreamPushNotificationToggle />
              </Slot>
            );
          case 'outfitSlider': {
            if (enableOutfitSlider) {
              return (
                <ErrorBoundary
                  key={`outfitSlider-${streamPosition}`}
                  ErrorComponent={() => null}
                  renderContext="streamView_outfitSlider"
                >
                  <OutfitSliderTile
                    streamPosition={streamPosition}
                    outfitSliderData={tile.type.outfitSlider}
                    gridColumns={gridColumns}
                  />
                </ErrorBoundary>
              );
            }
            return null;
          }
          case 'highlightsSection':
            return (
              <Slot
                columns={gridColumns}
                position={streamPosition}
                key={`highlightsSection-${streamPosition}`}
                Comp="li"
              >
                <BrandPageHighlights
                  streamPosition={streamPosition}
                  highlightsSection={tile.type.highlightsSection}
                  hideSideButton={layoutConfig?.hideSliderSideButtons}
                />
              </Slot>
            );
          case 'storyTeasersSection':
            // brand page
            return (
              <ErrorBoundary
                key={`storyTeasersSection-${streamPosition}`}
                ErrorComponent={() => null}
                renderContext="streamView_teaserCarousel"
              >
                <TeaserCarousel
                  gridColumns={gridColumns}
                  streamPosition={streamPosition}
                  teaser={tile.type.storyTeasersSection.slide.map(slide => ({
                    ...slide.teaser,
                    tracker: {name: 'personalized_brand_stories_reco'},
                  }))}
                  textPosition={
                    layoutConfig?.layout === 'desktop'
                      ? TeaserTileCarousel_TextPosition.OVER_IMAGE
                      : TeaserTileCarousel_TextPosition.BELOW_IMAGE
                  }
                  headline={tile.type.storyTeasersSection.headline}
                  subline={tile.type.storyTeasersSection.subline}
                  constrainStoryTilesToViewportBoundary={
                    constrainStoryTilesToViewportBoundary
                  }
                  uniqueKey={`streamView_teaserCarousel_${streamPosition}`}
                  shallowRenderingState={shallowRenderingState}
                />
              </ErrorBoundary>
            );
          case 'outfitSliderSection': {
            // brand page - mWeb
            if (!enableOutfitSlider) {
              return null;
            }

            return (
              <ErrorBoundary
                key={`outfitSlider-${streamPosition}`}
                ErrorComponent={() => null}
                renderContext="streamView_outfitSlider"
              >
                <OutfitSliderTile
                  streamPosition={streamPosition}
                  outfitSliderData={tile.type.outfitSliderSection}
                  gridColumns={gridColumns}
                />
              </ErrorBoundary>
            );
          }
          // used on category page
          case 'outfitRecommendation':
            return (
              <ErrorBoundary
                key={`outfitSlider-${streamPosition}`}
                ErrorComponent={() => null}
                renderContext="streamView_outfitSlider"
              >
                <OutfitTile
                  key={`outfitRecommendation-${tile.type.outfitRecommendation.outfit.id}`}
                  image={tile.type.outfitRecommendation.outfit.images[0]!}
                  streamPosition={streamPosition}
                  cta={{
                    linkTarget: {
                      url: tile.type.outfitRecommendation.outfit.path,
                      external: false,
                    },
                    label: t('View outfit'),
                  }}
                  source={TrackingPromotionCreative.OutfitReco}
                  outfit={tile.type.outfitRecommendation.outfit}
                  idol={tile.type.outfitRecommendation.idol}
                />
              </ErrorBoundary>
            );
          case 'outfitSection':
            // brand page - desktop
            return (
              <ErrorBoundary
                key={`outfitSlider-${streamPosition}`}
                ErrorComponent={() => null}
                renderContext="streamView_outfitSlider"
              >
                <OutfitTile
                  {...tile.type.outfitSection}
                  source={TrackingPromotionCreative.OutfitSliderBrand}
                  streamPosition={streamPosition}
                />
              </ErrorBoundary>
            );

          case 'heroTileSection': {
            return (
              <Slot
                columns={gridColumns}
                position={streamPosition}
                key={`heroTile-${streamPosition}`}
              >
                <HeroElementTile
                  streamPosition={streamPosition}
                  headline={tile.type.heroTileSection.headline?.text}
                  text={tile.type.heroTileSection.body}
                  media={tile.type.heroTileSection.media}
                  backgroundColor={
                    (
                      tile.type.heroTileSection.backgroundColorExtracted ??
                      tile.type.heroTileSection.backgroundColorManual
                    ).hex
                  }
                  cta={tile.type.heroTileSection.cta}
                  layout={layoutConfig?.layout || 'mobile'}
                  promotionTracker={tile.type.heroTileSection.promotionTracker}
                />
              </Slot>
            );
          }
          case 'heroProductSliderSection': {
            return (
              <Slot
                columns={gridColumns}
                position={streamPosition}
                key={`heroSlider-${streamPosition}`}
              >
                <HeroElementSlider
                  streamPosition={streamPosition}
                  headline={tile.type.heroProductSliderSection.headline?.text}
                  text={tile.type.heroProductSliderSection.body}
                  media={tile.type.heroProductSliderSection.media}
                  backgroundColor={
                    (
                      tile.type.heroProductSliderSection
                        .backgroundColorExtracted ??
                      tile.type.heroProductSliderSection.backgroundColorManual
                    ).hex
                  }
                  productTiles={tile.type.heroProductSliderSection.productTiles}
                  layout={layoutConfig?.layout || 'mobile'}
                  promotionTracker={
                    tile.type.heroProductSliderSection.promotionTracker
                  }
                  uniqueKey={`streamView_heroProductSliderSection_${streamPosition}`}
                />
              </Slot>
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
};

// This function helps to pass the importance of the loading state of the images.
// The prop gridColumns is used to check the grid layout, when nothing is passed
// means the grid will adapt to the viewport, for that case we define the importance
// as high after the second product, allowing the first 2 product tiles to load fast.
const imageLoadImportance = ({
  streamPosition,
  gridColumns,
}: {
  streamPosition: number;
  gridColumns: number | undefined;
}): ImageProps['importance'] => {
  const columns = gridColumns ?? 2;

  if (streamPosition < columns) {
    return 'high';
  }

  return 'auto';
};

const MemoizedStreamTileList = memo(StreamTileList, (a, b) => {
  if ('itemsPreferences' in a && 'itemsPreferences' in b) {
    const {itemsPreferences: itemsPreferencesA, ...restA} = a;
    const {itemsPreferences: itemsPreferencesB, ...restB} = b;
    return (
      shallowEqual(restA, restB) &&
      shallowEqual(itemsPreferencesA, itemsPreferencesB)
    );
  }

  return shallowEqual(a, b);
});

const useProductNamesABTest = (isCategoryPage: boolean) => {
  const activateApiAbTest = useActivateApiAbTestCallback();

  useEffect(() => {
    if (isCategoryPage) {
      activateApiAbTest('ab_product_names_cp_d');
      activateApiAbTest('ab_product_names_cp_m');
    }
  }, [isCategoryPage, activateApiAbTest]);
};

const advertisementStyles = css<{$increasePadding: boolean}>`
  ${props =>
    props.$increasePadding &&
    css`
      padding: 8px;
    `}

  ${NonProductTileSpacingAdjustment}
`;

const StyledStreamAdvertisementNormalized = styled(
  StreamAdvertisementNormalized,
)<{$increasePadding: boolean}>`
  ${advertisementStyles};
`;

const StyledCategoryTeaser = styled(CategoryTeaser)`
  ${advertisementStyles};
`;

export {MemoizedStreamTileList as StreamTileList};
